import { ErrorPage, InfoCardInner, Link, LoadingPage, Modal } from '@chocolate-soup-inc/cs-frontend-components';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { getGiftRecipientType } from '../../entities/gifts/utils';
import { StatusCell, TrackingLink } from '../../entities/shipments/utils';
import { SHIPMENTS_PATH, SHIPMENT_PATH } from '../../routes/paths';
import { EmployeeModal } from './EmployeeModal';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { toast } from 'react-toastify';
import { useQueryOneEmployee } from '../../entities/employees/queries';
import { useQueryAllEmployeeAndDependantsGifts } from '../../entities/gifts/queries';

export const EmployeeGifts = () => {
  const navigate = useNavigate();
  const { employeeId = '0' } = useParams();

  const {
    data: employee,
    error,
    loading
  } = useQueryOneEmployee({
    id: employeeId
  });

  const title = useMemo(() => {
    const fullName = employee?.fullName;
    if (fullName) return `${fullName} and Dependants Gifts`;
    else return 'Employee and Dependants Dependants';
  }, [employee]);

  const {
    data: gifts,
    error: giftsError,
    loading: giftsLoading
  } = useQueryAllEmployeeAndDependantsGifts({
    employeeId,
    companyId: employee.companyId
  });

  const closeModal = useCallback(() => {
    navigate(generatePath(`../../`));
  }, [navigate]);

  const goToShipment = useCallback(
    (gift?: (typeof gifts)[number]) => {
      if (gift?.shipment)
        navigate(generatePath(`${SHIPMENTS_PATH}/${SHIPMENT_PATH}`, { shipmentId: gift.shipment.id }), {
          state: { backPath: window.location.pathname, closePath: closeModal }
        });
    },
    [navigate, closeModal]
  );

  if (loading || giftsLoading)
    return (
      <Modal closeModal={closeModal} headline={title}>
        <LoadingPage />
      </Modal>
    );

  if (error || giftsError) {
    console.error('query Apollo ERROR:', error);
    console.error('query Apollo ERROR:', giftsError);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    //if (giftsError.message) toast.error(giftsError.message);
    if (!gifts)
      return (
        <Modal closeModal={closeModal} headline={title}>
          <ErrorPage error={error || giftsError} />
        </Modal>
      );
  }

  return (
    <EmployeeModal employee={employee} headline={title}>
      <div>
        {gifts.length === 0 && <p>No gifts are present for this employee.</p>}
        {gifts.map((gift, index) => (
          <InfoCardInner<(typeof gifts)[number]>
            key={gift.id}
            data={gift}
            sections={[
              {
                title: `Gift #${gifts.length - index}`,
                columns: [
                  {
                    label: 'Recipient Name',
                    render: (data) => data?.recipient?.fullName
                  },
                  {
                    label: 'Type',
                    render: (data) => data?.type
                  },
                  {
                    label: 'Event Date',
                    render: (data) => readableDate(data?.eventDate)
                  },
                  {
                    label: 'Event Notes',
                    render: () => gift.notes
                  },
                  {
                    label: 'Relationship',
                    render: () => getGiftRecipientType(gift)
                  }
                ]
              },
              {
                columns: [
                  {
                    label: 'Status',
                    render: () => StatusCell(gift.shipment || undefined)
                  },
                  {
                    label: 'Shipping Date',
                    render: (data) => readableDate(data?.shipment?.actualShippingDate || data?.shipment?.shippingDate || undefined)
                  },
                  {
                    label: 'Tracking Number',
                    render: () => (gift.shipment ? TrackingLink(gift.shipment) : null)
                  },
                  {
                    label: 'Shipment',
                    render: () => {
                      if (gift.shipment) return <Link onClick={() => goToShipment(gift)} label='See Shipment Details' />;
                      else return null;
                    }
                  }
                ]
              }
            ]}
          />
        ))}
      </div>
    </EmployeeModal>
  );
};
